import { ctaDesc, ctaTitle } from "@/lib/seo";
import { blogCover } from "@/data/blog-covers";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import type { BLOG_HEAD as BlogHeadMap } from "@/data/blog-head.th.gen";

type BlogHeadEntry = (typeof BlogHeadMap)[string];
import { speakableSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { getAboutEntities } from "@/data/blog-entity-map";
import { BlogArticleTh } from "@/components/BlogArticleTh";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const { BLOG_HEAD } = await import("@/data/blog-head.th.gen");
    const meta = BLOG_HEAD[params.slug];
    if (!meta) throw notFound();
    const { BLOG_BODY } = await import("@/data/blog-body.th.gen");
    return { slug: params.slug, articleBody: BLOG_BODY[params.slug] ?? "", meta: meta as BlogHeadEntry };
  },
  head: ({ params, loaderData }) => {
    const p = loaderData?.meta;
    if (!p) return { meta: [{ title: ctaTitle("ไม่พบบทความ", "/blog/$slug", "th")}] };
    const cover = blogCover(p.category, "th");
    const url = `${SITE}/blog/${p.slug}`;
    return {
      meta: [
        { title: ctaTitle(p.metaTitle, "/blog/$slug", "th")},
        { name: "description", content: ctaDesc(p.metaDesc, "/blog/$slug", "th")},
        { name: "keywords", content: p.tags.join(", ") },
        { name: "author", content: p.author },
        { property: "og:title", content: ctaTitle(p.metaTitle, "/blog/$slug", "th")},
        { property: "og:description", content: ctaDesc(p.metaDesc, "/blog/$slug", "th")},
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "th_TH" },
        { property: "article:published_time", content: p.publishedAt },
        { property: "article:modified_time", content: p.updatedAt },
        { property: "article:author", content: p.author },
        { property: "article:section", content: p.category },
        { property: "og:image", content: cover.absolute },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: cover.altTh },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ctaTitle(p.metaTitle, "/blog/$slug", "th")},
        { name: "twitter:description", content: p.metaDesc },
        { name: "twitter:image", content: cover.absolute },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
        { rel: "alternate", hrefLang: "en", href: url.replace(SITE, `${SITE}/en`) },
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "@id": `${url}#article`,
              headline: p.title,
              description: p.metaDesc,
              image: [cover.absolute],
              inLanguage: "th",
              datePublished: p.publishedAt,
              dateModified: p.updatedAt,
              author: {
                "@type": "Person",
                "@id": `${SITE}/about/team#${p.author.replace(/\s+/g, "-").toLowerCase()}`,
                name: p.author,
                url: `${SITE}/about/team`,
                worksFor: { "@id": `${SITE}/#organization` },
              },
              publisher: { "@id": `${SITE}/#organization` },
              mainEntityOfPage: { "@type": "WebPage", "@id": url },
              keywords: p.tags.join(", "),
              articleSection: p.category,
              wordCount: p.wordCount,
              articleBody: loaderData?.articleBody ?? "",
              timeRequired: `PT${p.readMinutes}M`,
              isAccessibleForFree: true,
              isPartOf: { "@id": `${SITE}/blog#collection` },
              about: getAboutEntities(p.slug, "th"),
              mentions: getAboutEntities(p.slug, "th"),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
                { "@type": "ListItem", position: 3, name: p.title, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: BlogPostPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบบทความ</h1>
      <Link to="/blog" className="mt-4 inline-block text-gold underline">กลับไปยัง Knowledge Hub</Link>
    </main>
  ),
});

function BlogPostPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  return <BlogArticleTh slug={slug} />;
}
